import React, { useState, useEffect, useContext } from 'react'
import { Table, Popconfirm, notification, Tag, Modal } from 'antd'
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { GET_KEYWORD_MAPPING_BY_DEPARTMENT_AND_TEAM } from '../graphql/Queries'
import {
  CREATE_KEYWORD_MAPPING,
  UPDATE_KEYWORD_MAPPING,
  DELETE_KEYWORD_MAPPING,
} from '../graphql/Mutations'
import KeywordMappingModal from './KeywordMappingModal'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserDelete } from 'utils/User'

const getPriorityColor = (priority) => {
  switch (priority) {
    case 'HIGH':
      return 'red'
    case 'MEDIUM':
      return 'orange'
    case 'LOW':
      return 'green'
    default:
      return 'default'
  }
}

export default function EditKeywordMappingModel({
  visible,
  onClose,
  initialValues,
  parentRefetch,
}) {
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const canAdd = canUserAdd(userPermissions || currentUser, isClient)
  const canDelete = canUserDelete(userPermissions || currentUser, isClient)

  const [keywordModalVisible, setKeywordModalVisible] = useState(false)
  const [editingKeyword, setEditingKeyword] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)

  const {
    data,
    loading: queryLoading,
    refetch,
  } = useQuery(GET_KEYWORD_MAPPING_BY_DEPARTMENT_AND_TEAM, {
    variables: {
      departmentId: initialValues?.department?.id,
      teamId: initialValues?.team?.id,
    },
    skip:
      !visible || !initialValues?.department?.id || !initialValues?.team?.id,
  })

  const [createKeywordMapping] = useMutation(CREATE_KEYWORD_MAPPING)
  const [updateKeywordMapping] = useMutation(UPDATE_KEYWORD_MAPPING)
  const [deleteKeywordMapping] = useMutation(DELETE_KEYWORD_MAPPING)

  const [keywordMappings, setKeywordMappings] = useState([])

  useEffect(() => {
    if (data && data.getKeywordMappingsByDepartmentAndTeam) {
      setKeywordMappings(data.getKeywordMappingsByDepartmentAndTeam)
    }
  }, [data])

  console.log('keywordMappings in edit modal', keywordMappings)

  const handleEditKeyword = (record) => {
    if (!canEdit) return
    setEditingKeyword(record)
    setKeywordModalVisible(true)
  }

  const handleDeleteKeyword = async (id) => {
    if (!canDelete) return
    try {
      await deleteKeywordMapping({ variables: { id } })
      notification.success({ message: 'Keyword deleted' })
      refetch()
      parentRefetch()
    } catch (err) {
      notification.error({ message: 'Delete failed' })
    }
  }

  const handleKeywordModalOk = async (values) => {
    if (editingKeyword && !canEdit) return
    if (!editingKeyword && !canAdd) return
    setModalLoading(true)
    try {
      if (editingKeyword) {
        await updateKeywordMapping({
          variables: { id: editingKeyword.id, data: values },
        })
        notification.success({ message: 'Keyword updated' })
      } else {
        await createKeywordMapping({
          variables: {
            data: {
              ...values,
              departmentId: initialValues.department.id,
              teamId: initialValues.team.id,
            },
          },
        })
        notification.success({ message: 'Keyword created' })
      }
      setKeywordModalVisible(false)
      refetch()
      parentRefetch() // refresh parent 👈
    } catch (err) {
      notification.error({ message: 'Operation failed' })
    } finally {
      setModalLoading(false)
    }
  }

  //const handleToggleActive = async (record) => {
  //  try {
  //    await updateKeywordMapping({
  //      variables: {
  //        id: record.id,
  //        data: {
  //          isActive: !record.isActive,
  //        },
  //      },
  //    })
  //    notification.success({
  //      message: `Keyword mapping ${
  //        record.isActive ? 'deactivated' : 'activated'
  //      }`,
  //    })
  //    refetch()
  //  } catch (err) {
  //    notification.error({ message: 'Update failed' })
  //  }
  //}

  const columns = [
    {
      title: 'Keyword',
      dataIndex: 'keyword',
      key: 'keyword',
      render: (keyword) => (
        <Tag color='blue' style={{ fontSize: '14px', padding: '4px 8px' }}>
          {keyword}
        </Tag>
      ),
    },
    {
      title: 'Priority',
      dataIndex: 'suggestsPriority',
      key: 'suggestsPriority',
      render: (suggestsPriority) => (
        <Tag
          color={getPriorityColor(
            suggestsPriority?.priorityLevel?.toUpperCase()
          )}
          style={{ fontWeight: 500 }}
        >
          {suggestsPriority?.priorityLevel}
        </Tag>
      ),
    },
    ...((canEdit || canDelete)
      ? [
          {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
              <>
                {canEdit && (
                  <EditOutlined
                    color='primary'
                    style={{ marginRight: 12, cursor: 'pointer' }}
                    onClick={() => handleEditKeyword(record)}
                  />
                )}
                {canDelete && (
                  <Popconfirm
                    title='Delete?'
                    onConfirm={() => handleDeleteKeyword(record.id)}
                  >
                    <DeleteOutlined color='red' style={{ cursor: 'pointer' }} />
                  </Popconfirm>
                )}
              </>
            ),
          },
        ]
      : []),
  ]

  if (!visible || !initialValues) return null

  return (
    <Modal
      title={`Edit Keywords - ${initialValues.department?.departmentName} / ${initialValues.team?.teamName}`}
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
    >
      <div style={{ padding: 5 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        ></div>

        <Table
          columns={columns}
          dataSource={keywordMappings}
          rowKey='id'
          loading={queryLoading}
          pagination={false}
          size='small'
        />

        <KeywordMappingModal
          visible={keywordModalVisible}
          onClose={() => setKeywordModalVisible(false)}
          onSave={handleKeywordModalOk}
          initialValues={editingKeyword}
          loading={modalLoading}
          isEditMode={true}
          departmentId={initialValues.department?.id}
          teamId={initialValues.team?.id}
        />
      </div>
    </Modal>
  )
}
